import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Sparkles, ArrowLeft, Crown, Upload, Check } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '@/components/Footer';

const LOGO_URL = '/assets/hero/logo.png';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_GROUPS = ['Children', 'Teens', 'Adults', 'Seniors'];

const emptyIntake = {
  client_types: '',
  age_groups: [],
  service_format: '',
  service_locations: '',
  languages: '',
  accepts_insurance: false,
  insurance_plans: '',
  sliding_scale: false,
  sliding_scale_details: '',
  cultural_influence: '',
  communities_served: '',
  cultural_care_meaning: '',
  why_healer: '',
  health_disparities: '',
  desired_change: '',
  client_experience: '',
  short_bio: '',
  long_bio: '',
  social_handles: '',
  headshot_path: null,
  logo_path: null,
  testimonials: '',
  credentials: '',
  years_practice: 0,
  associations: '',
  website: '',
  contact_email: '',
  agrees_code_of_conduct: false,
  wisdom_for_poc: '',
};

const SectionHeading = ({ children }) => (
  <div className="border-b border-amber-200 pb-2 mb-4 mt-2">
    <h3 className="text-lg font-bold" style={{ color: '#8B4513' }}>{children}</h3>
  </div>
);

const Field = ({ label, required, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-stone-800">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </Label>
    {children}
    {hint && <p className="text-xs text-stone-500">{hint}</p>}
  </div>
);

const Founders = () => {
  const navigate = useNavigate();
  const { code } = useParams();
  const { registerFounder } = useAuth();

  const [verifying, setVerifying] = useState(true);
  const [codeValid, setCodeValid] = useState(false);

  // Account
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('healer');

  // Intake
  const [intake, setIntake] = useState(emptyIntake);
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const verify = async () => {
      try {
        await axios.get(`${API}/auth/founders/verify`, { params: { code } });
        setCodeValid(true);
      } catch {
        setCodeValid(false);
      } finally {
        setVerifying(false);
      }
    };
    verify();
  }, [code]);

  const update = (field, value) => setIntake((prev) => ({ ...prev, [field]: value }));
  const toggleAgeGroup = (g) => {
    setIntake((prev) => ({
      ...prev,
      age_groups: prev.age_groups.includes(g) ? prev.age_groups.filter((x) => x !== g) : [...prev.age_groups, g],
    }));
  };

  const uploadImage = async (file, kind) => {
    if (!file) return null;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return null;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return null;
    }
    const setLoading = kind === 'headshot' ? setUploadingHeadshot : setUploadingLogo;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('files', file);
      const res = await axios.post(`${API}/upload/application-photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const path = res.data.paths?.[0];
      if (path) {
        update(kind === 'headshot' ? 'headshot_path' : 'logo_path', path);
        toast.success(`${kind === 'headshot' ? 'Headshot' : 'Logo'} uploaded`);
      }
      return path;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !password.trim()) {
      toast.error('Please fill in name, email, and password');
      return;
    }
    if (role === 'healer' && !intake.agrees_code_of_conduct) {
      toast.error("Please agree to Ourmend's Code of Conduct to continue");
      return;
    }
    setSubmitting(true);
    try {
      const payload = role === 'healer'
        ? { ...intake, contact_email: intake.contact_email || email }
        : null;
      const userData = await registerFounder(code, email, password, name, role, payload);
      toast.success('Welcome, Founding Member!');
      navigate(userData.role === 'healer' ? '/healer-dashboard' : '/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-100 flex flex-col">
      <Helmet>
        <title>Founding Member Invite | Ourmend</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <nav className="bg-white/80 backdrop-blur-lg border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center space-x-2 sm:space-x-3" data-testid="founders-logo-link">
            <img src={LOGO_URL} alt="Ourmend Logo" className="h-9 w-9 sm:h-12 sm:w-12 object-contain" />
            <div className="text-left">
              <span className="text-lg sm:text-2xl font-bold" style={{ color: '#8B4513' }}>Ourmend</span>
              <p className="text-[10px] sm:text-xs text-stone-600 font-bold">Community Healers. Cultural Care.</p>
            </div>
          </button>
          <Button variant="outline" onClick={() => navigate('/')} data-testid="founders-home-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Home
          </Button>
        </div>
      </nav>

      <main className="flex-1 px-4 sm:px-6 py-10 sm:py-14">
        <div className="max-w-3xl mx-auto">
          {verifying ? (
            <Card className="p-8 text-center">
              <p className="text-stone-600">Verifying your invite...</p>
            </Card>
          ) : !codeValid ? (
            <Card className="p-8 text-center max-w-md mx-auto" data-testid="founders-invalid">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-stone-100 mb-4">
                <Crown className="w-7 h-7 text-stone-400" />
              </div>
              <h1 className="text-2xl font-bold mb-2" style={{ color: '#3E2723' }}>Invite Not Valid</h1>
              <p className="text-stone-600 mb-6">
                This founding-member invite link is either invalid or has expired. Please double-check the URL or contact the person who shared it with you.
              </p>
              <Button onClick={() => navigate('/')} className="bg-[#8B4513] hover:bg-[#6B3410] text-white" data-testid="founders-go-home-btn">
                Go to Homepage
              </Button>
            </Card>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 shadow-lg mb-4">
                  <Crown className="w-8 h-8 text-white" />
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 border border-amber-200 mb-3">
                  <Sparkles className="w-3.5 h-3.5 text-amber-700" />
                  <span className="text-xs font-bold uppercase tracking-wide text-amber-800">Founding Member Invite</span>
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: '#3E2723' }}>
                  You&apos;re invited to join Ourmend
                </h1>
                <p className="text-base text-stone-700 max-w-2xl mx-auto">
                  As a founding member, your account is complimentary — <strong>no subscription, no fees, ever</strong>. Welcome to the community.
                </p>
              </div>

              <Card className="p-6 sm:p-8 bg-white shadow-xl border-amber-200" data-testid="founders-form-card">
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="founders-form">
                  {/* Account */}
                  <SectionHeading>Account</SectionHeading>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Full Name" required>
                      <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="founder-name-input" />
                    </Field>
                    <Field label="Email" required>
                      <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="founder-email-input" />
                    </Field>
                  </div>
                  <Field label="Password" required hint="At least 6 characters">
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="founder-password-input" />
                  </Field>
                  <Field label="I am a" required>
                    <select
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white"
                      data-testid="founder-role-select"
                    >
                      <option value="healer">Healer (free lifetime listing)</option>
                      <option value="patient">Patient</option>
                    </select>
                  </Field>

                  {role === 'healer' && (
                    <>
                      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                        ✨ As a founding healer, your listing will be granted lifetime access automatically — no Stripe, no monthly fee. Please fill out the intake below so we can publish your profile.
                      </p>

                      <SectionHeading>Practice &amp; Services</SectionHeading>
                      <Field label="What types of clients do you primarily serve?">
                        <Textarea rows={2} value={intake.client_types} onChange={(e) => update('client_types', e.target.value)} data-testid="intake-client-types" />
                      </Field>
                      <Field label="What age groups do you work with?">
                        <div className="flex flex-wrap gap-2">
                          {AGE_GROUPS.map((g) => {
                            const selected = intake.age_groups.includes(g);
                            return (
                              <button
                                type="button"
                                key={g}
                                onClick={() => toggleAgeGroup(g)}
                                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${selected ? 'bg-[#8B4513] text-white border-[#8B4513]' : 'bg-white text-stone-700 border-stone-300 hover:border-[#8B4513]'}`}
                                data-testid={`intake-age-${g.toLowerCase()}`}
                              >
                                {selected && <Check className="w-3 h-3 inline mr-1" />}
                                {g}
                              </button>
                            );
                          })}
                        </div>
                      </Field>
                      <Field label="Do you offer virtual services, in-person services, or both?">
                        <select
                          value={intake.service_format}
                          onChange={(e) => update('service_format', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white"
                          data-testid="intake-service-format"
                        >
                          <option value="">Select...</option>
                          <option value="virtual">Virtual only</option>
                          <option value="in_person">In-person only</option>
                          <option value="both">Both virtual and in-person</option>
                        </select>
                      </Field>
                      <Field label="What states/countries are you licensed or available to serve in?">
                        <Input value={intake.service_locations} onChange={(e) => update('service_locations', e.target.value)} placeholder="e.g., California, New York, Online (US)" data-testid="intake-service-locations" />
                      </Field>
                      <Field label="What languages do you speak?" hint="Comma-separated">
                        <Input value={intake.languages} onChange={(e) => update('languages', e.target.value)} placeholder="English, Spanish, Yoruba..." data-testid="intake-languages" />
                      </Field>
                      <Field label="Do you accept insurance?">
                        <div className="flex items-center gap-3">
                          <label className="inline-flex items-center gap-2">
                            <input type="checkbox" checked={intake.accepts_insurance} onChange={(e) => update('accepts_insurance', e.target.checked)} data-testid="intake-accepts-insurance" />
                            <span className="text-sm">Yes, I accept insurance</span>
                          </label>
                        </div>
                        {intake.accepts_insurance && (
                          <Input className="mt-2" value={intake.insurance_plans} onChange={(e) => update('insurance_plans', e.target.value)} placeholder="Plans accepted (e.g., Aetna, Blue Cross, Medicare)" data-testid="intake-insurance-plans" />
                        )}
                      </Field>
                      <Field label="Do you offer sliding scale or low-cost services?">
                        <label className="inline-flex items-center gap-2">
                          <input type="checkbox" checked={intake.sliding_scale} onChange={(e) => update('sliding_scale', e.target.checked)} data-testid="intake-sliding-scale" />
                          <span className="text-sm">Yes</span>
                        </label>
                        {intake.sliding_scale && (
                          <Textarea className="mt-2" rows={2} value={intake.sliding_scale_details} onChange={(e) => update('sliding_scale_details', e.target.value)} placeholder="Briefly describe your sliding scale or low-cost options" data-testid="intake-sliding-scale-details" />
                        )}
                      </Field>

                      <SectionHeading>Cultural Alignment</SectionHeading>
                      <Field label="How does your cultural background influence your approach to healing?">
                        <Textarea rows={3} value={intake.cultural_influence} onChange={(e) => update('cultural_influence', e.target.value)} data-testid="intake-cultural-influence" />
                      </Field>
                      <Field label="What communities do you feel particularly equipped to serve?">
                        <Textarea rows={2} value={intake.communities_served} onChange={(e) => update('communities_served', e.target.value)} data-testid="intake-communities-served" />
                      </Field>
                      <Field label="What does culturally aligned care mean to you?">
                        <Textarea rows={3} value={intake.cultural_care_meaning} onChange={(e) => update('cultural_care_meaning', e.target.value)} data-testid="intake-cultural-care-meaning" />
                      </Field>

                      <SectionHeading>Mission &amp; Values</SectionHeading>
                      <Field label="Why did you become a healer?">
                        <Textarea rows={3} value={intake.why_healer} onChange={(e) => update('why_healer', e.target.value)} data-testid="intake-why-healer" />
                      </Field>
                      <Field label="What health disparities concern you most?">
                        <Textarea rows={2} value={intake.health_disparities} onChange={(e) => update('health_disparities', e.target.value)} data-testid="intake-health-disparities" />
                      </Field>
                      <Field label="What change would you most like to see in healthcare for communities of color?">
                        <Textarea rows={2} value={intake.desired_change} onChange={(e) => update('desired_change', e.target.value)} data-testid="intake-desired-change" />
                      </Field>
                      <Field label="What do you hope clients experience after working with you?">
                        <Textarea rows={2} value={intake.client_experience} onChange={(e) => update('client_experience', e.target.value)} data-testid="intake-client-experience" />
                      </Field>

                      <SectionHeading>Profile &amp; Marketing</SectionHeading>
                      <Field label="Short bio" hint="100–200 words">
                        <Textarea rows={4} value={intake.short_bio} onChange={(e) => update('short_bio', e.target.value)} data-testid="intake-short-bio" />
                      </Field>
                      <Field label="Longer bio" hint="Up to 500 words">
                        <Textarea rows={6} value={intake.long_bio} onChange={(e) => update('long_bio', e.target.value)} data-testid="intake-long-bio" />
                      </Field>
                      <Field label="Social media handles" hint="Instagram, Twitter/X, LinkedIn, etc.">
                        <Textarea rows={2} value={intake.social_handles} onChange={(e) => update('social_handles', e.target.value)} placeholder="@instagram_handle, linkedin.com/in/..., ..." data-testid="intake-social-handles" />
                      </Field>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Professional headshot" hint="JPEG/PNG/WebP, up to 5MB">
                          <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border border-stone-300 bg-white hover:bg-stone-50 cursor-pointer text-sm ${uploadingHeadshot ? 'opacity-50' : ''}`}>
                            <Upload className="w-4 h-4" />
                            {uploadingHeadshot ? 'Uploading...' : (intake.headshot_path ? 'Replace' : 'Upload headshot')}
                            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadImage(e.target.files[0], 'headshot')} data-testid="intake-headshot-input" />
                          </label>
                          {intake.headshot_path && (
                            <img src={`${API}/files/${intake.headshot_path}`} alt="Headshot preview" className="mt-2 w-20 h-20 object-cover rounded-full border" />
                          )}
                        </Field>
                        <Field label="Logo (optional)">
                          <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border border-stone-300 bg-white hover:bg-stone-50 cursor-pointer text-sm ${uploadingLogo ? 'opacity-50' : ''}`}>
                            <Upload className="w-4 h-4" />
                            {uploadingLogo ? 'Uploading...' : (intake.logo_path ? 'Replace' : 'Upload logo')}
                            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadImage(e.target.files[0], 'logo')} data-testid="intake-logo-input" />
                          </label>
                          {intake.logo_path && (
                            <img src={`${API}/files/${intake.logo_path}`} alt="Logo preview" className="mt-2 w-20 h-20 object-contain rounded border bg-white" />
                          )}
                        </Field>
                      </div>
                      <Field label="Do you have client testimonials you would like featured?" hint="Optional — paste them here">
                        <Textarea rows={3} value={intake.testimonials} onChange={(e) => update('testimonials', e.target.value)} data-testid="intake-testimonials" />
                      </Field>

                      <SectionHeading>Trust &amp; Verification</SectionHeading>
                      <Field label="Credentials, licenses, certifications, or training">
                        <Textarea rows={3} value={intake.credentials} onChange={(e) => update('credentials', e.target.value)} data-testid="intake-credentials" />
                      </Field>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Years in practice">
                          <Input type="number" min={0} value={intake.years_practice} onChange={(e) => update('years_practice', parseInt(e.target.value || '0', 10))} data-testid="intake-years-practice" />
                        </Field>
                        <Field label="Professional associations or affiliations">
                          <Input value={intake.associations} onChange={(e) => update('associations', e.target.value)} data-testid="intake-associations" />
                        </Field>
                      </div>
                      <Field label="Website">
                        <Input value={intake.website} onChange={(e) => update('website', e.target.value)} placeholder="https://..." data-testid="intake-website" />
                      </Field>
                      <Field label="Contact email" hint="Where prospective clients can reach you (defaults to your account email)">
                        <Input type="email" value={intake.contact_email} onChange={(e) => update('contact_email', e.target.value)} placeholder={email || 'you@example.com'} data-testid="intake-contact-email" />
                      </Field>

                      <SectionHeading>One more thing</SectionHeading>
                      <Field label="What do you wish people of color knew about healing?">
                        <Textarea rows={3} value={intake.wisdom_for_poc} onChange={(e) => update('wisdom_for_poc', e.target.value)} data-testid="intake-wisdom" />
                      </Field>

                      <label className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 cursor-pointer" data-testid="intake-code-of-conduct-label">
                        <input
                          type="checkbox"
                          checked={intake.agrees_code_of_conduct}
                          onChange={(e) => update('agrees_code_of_conduct', e.target.checked)}
                          required
                          data-testid="intake-code-of-conduct"
                          className="mt-0.5"
                        />
                        <span className="text-sm text-stone-800">
                          I agree to Ourmend&apos;s <a href="/terms" target="_blank" rel="noreferrer" className="underline font-medium">Code of Conduct &amp; Community Standards</a>. <span className="text-red-500">*</span>
                        </span>
                      </label>
                    </>
                  )}

                  <Button
                    type="submit"
                    disabled={submitting || uploadingHeadshot || uploadingLogo}
                    className="w-full h-12 bg-gradient-to-r from-[#8B4513] to-[#A0522D] hover:from-[#6B3410] hover:to-[#8B4513] text-white font-semibold text-base"
                    data-testid="founder-submit-btn"
                  >
                    {submitting ? 'Creating account...' : 'Claim Founding Membership'}
                  </Button>
                </form>
              </Card>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Founders;
